<template>
    <app-layout>
        <page-header :title="trans('assets.details.edit_asset')">
            <p class="m-0">
                {{ trans('assets.details.asset_identification.description') }}
            </p>
        </page-header>
        <row>
            <column>
                <jet-form-section type="decent" @submitted="handleSubmit">
                    <template #form>
                        <asset-information-form-section :form="form" @update:selected-model-option="onModelChange($event)" />
                        <section class="mt-4">
                            <h3 class="h4">
                                {{ trans('assets.details.default_testing_task.header') }}
                            </h3>
                            <p>{{ trans('assets.details.default_testing_task.description') }}</p>
                            <jet-select
                                v-model="form.defaultTestingTaskType"
                                :description="platformDefaultHint"
                                :disabled="!form.modelId || form.modelId !== loadedModelId"
                                :horizontal="false"
                                :invalidFeedback="form.errors.defaultTestingTaskType"
                                :options="testingTaskTypeOptions"
                                :placeholder="trans('assets.details.default_testing_task.placeholder')"
                                :reset-label="trans('assets.details.default_testing_task.placeholder')"
                                name="defaultTestingTaskType"
                            />
                        </section>
                    </template>
                    <template #actions>
                        <jet-form-buttons
                            :cancel-route="route('assets.show', asset.id)"
                            :disabled="!form.isDirty"
                            :has-changes="form.isDirty"
                            :is-processing="form.processing"
                            :is-update-form="true"
                            @submit="handleSubmit"
                        />
                    </template>
                </jet-form-section>
            </column>
        </row>
    </app-layout>
</template>
<script>
import AppLayout from '@/Layouts/AppLayout.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetFormButtons from '@/Jetstream/FormButtons.vue';
import JetSelect from '@/Jetstream/Select.vue';
import PageHeader from '@/Components/PageHeader.vue';
import { useUnsavedChangesWarning } from '@/Utils/UseUnsavedChangesWarning';
import AssetInformationFormSection from '@/Pages/Assets/Edit/AssetInformationFormSection.vue';

export default {
    components: {
        AssetInformationFormSection,
        PageHeader,
        AppLayout,
        JetFormSection,
        JetFormButtons,
        JetSelect,
    },

    data() {
        return {
            models: [],
            isLoadingModels: false,
            loadedModelId: null,
            form: this.$inertia.form({
                curieId: '',
                manufacturer: '',
                serialNumber: '',
                customerTrackingNumber: '',
                description: '',
                owner: '',
                operator: '',
                maintainer: '',
                locationId: '',
                modelId: '',
                screeningApplication: '',
                purchasedAt: '',
                deployedAt: '',
                critical: 'no',
                warrantyStartDate: '',
                warrantyEndDate: '',
                defaultTestingTaskType: '',
            }),
        };
    },

    computed: {
        asset() {
            return this.$page.props.asset;
        },
        testingTaskTypeOptions() {
            return this.$page.props.testingTaskTypes?.data ?? [];
        },
        platformDefaultHint() {
            const label = this.$page.props.platformDefaultTestingTaskType?.label;
            if (!label) return '';
            return this.trans('assets.details.default_testing_task.platform_default_hint', { task: label });
        },
    },

    mounted() {
        this.form = this.$inertia.form({
            curieId: this.asset.curieId || '',
            serialNumber: this.asset.serial_number || '',
            description: this.asset.description || '',
            customerTrackingNumber: this.asset.customer_tracking_number || '',
            owner: this.asset.owner?.id || '',
            operator: this.asset.operator?.id || '',
            manufacturer: this.asset.manufacturer?.id || '',
            maintainer: this.asset.maintainer?.id || '',
            locationId: this.asset.location?.id || '',
            modelId: this.asset.model?.id || '',
            screeningApplication: this.asset.screening_application?.value || '',
            purchasedAt: this.asset.purchased_at,
            deployedAt: this.asset.deployed_at,
            critical: this.asset.critical ? 'yes' : 'no',
            warrantyStartDate: this.asset.warranty?.start_date,
            warrantyEndDate: this.asset.warranty?.end_date,
            defaultTestingTaskType: this.$page.props.currentDefaultTestingTaskType ?? '',
        });

        this.selectedManufacturerId = this.asset.manufacturer.id;
        // The server already rendered the options for the asset's current model, so the
        // designation dropdown's initial async emit is a no-op (see refreshTestingTaskTypes).
        this.loadedModelId = this.asset.model?.id ?? null;

        useUnsavedChangesWarning({
            hasChanges: () => this.form.isDirty || this.form.processing,
            handleSubmit: this.handleSubmit,
        });
    },

    methods: {
        onModelChange(option) {
            this.refreshTestingTaskTypes(option?.value ?? null);
        },
        refreshTestingTaskTypes(modelId) {
            // Skip the dropdown's initial async emit; only refetch on a real model change.
            if (modelId === this.loadedModelId) {
                return;
            }
            // Invalidate the loaded marker up front so a re-selection mid-flight still passes
            // the guard and starts a fresh (superseding) reload, rather than being skipped and
            // letting a stale in-flight response commit last.
            this.loadedModelId = null;
            // Drop the previous model's default now so a stale value can't survive the
            // in-flight window (the select is also disabled until the new options load).
            this.form.defaultTestingTaskType = '';
            this.$inertia.reload({
                only: ['testingTaskTypes'],
                data: { modelId: modelId ?? '' },
                preserveScroll: true,
                preserveState: true,
                // Mark loaded only on success so a failed reload can retry.
                onSuccess: () => {
                    this.loadedModelId = modelId;
                },
            });
        },
        handleSubmit(event) {
            return new Promise((resolve) => {
                this.form.transform(this.transformData).put(
                    this.route('assets.update', {
                        asset: this.asset,
                        redirect: event?.redirectUrl?.toString(),
                    }),
                    {
                        onSuccess: () => resolve(true),
                        onError: () => resolve(false),
                    },
                );
            });
        },
        transformData(data) {
            data = Object.assign({}, data);

            return {
                ...data,
                critical: data.critical === 'yes',
                defaultTestingTaskType: data.defaultTestingTaskType || null,
            };
        },
    },
};
</script>
